import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  TextField,
  Box,
  InputAdornment,
} from "@mui/material";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import { useTranslation } from "react-i18next";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomCard from "../../components/Famer/GenericCard";
import { useAlert } from "../../components/Alert";
import { getManagedFranchises, createFranchise } from "../../api/franchise";
import { handleMobileKeyDown } from "../../globals/utils";

export default function FranchiseManagement() {
  const [franchises, setFranchises] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    basic_subscription_price: 1000,
    premium_subscription_price: 1200,
    soiltest_price: 200,
    basic_subscription_incentive: 400,
    premium_subscription_incentive: 350,
    soiltest_incentive: 50,
    bank_account: null,
    owner_full_name: "", // Added for owner details
    owner_phone: "", // Added for owner details
  });
  // No need for success details state anymore as we won't show the confirmation popup

  const { showAlert } = useAlert();
  const { t } = useTranslation();

  useEffect(() => {
    fetchFranchisesAndStats();
  }, []);

  const fetchFranchisesAndStats = async () => {
    setLoading(true);
    try {
      const [franchisesData, statsData] = await Promise.all([
        getManagedFranchises(),
      ]);

      setFranchises(franchisesData);
      setStats(statsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching franchise data:", error);
      setLoading(false);
      showAlert("error", "Error fetching franchise data");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name.includes("price") || name.includes("incentive")
          ? parseFloat(value)
          : value,
    });
  };

  const validateForm = () => {
    // Validate required fields
    if (!formData.name || !formData.owner_full_name || !formData.owner_phone) {
      showAlert("error", "Franchise name, owner name and phone are required");
      return false;
    }

    // Basic phone validation
    if (!/^\d{10}$/.test(formData.owner_phone)) {
      showAlert("error", "Please enter a valid 10-digit phone number");
      return false;
    }

    return true;
  };

  const handleAddFranchise = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await createFranchise(formData);
      showAlert(
        "success",
        "Franchise created successfully. Username and password are phone number."
      );
      setFormData({
        name: "",
        basic_subscription_price: 1000,
        premium_subscription_price: 1200,
        soiltest_price: 200,
        basic_subscription_incentive: 400,
        premium_subscription_incentive: 350,
        soiltest_incentive: 50,
        bank_account: null,
        owner_full_name: "",
        owner_phone: "",
      });
      setShowAddForm(false);

      fetchFranchisesAndStats();
    } catch (error) {
      showAlert(
        "error",
        error.response?.data?.error || "Error creating franchise"
      );
      setLoading(false);
    }
  };

  // No longer need handleCloseSuccessDetails

  // const handleViewFranchise = (id) => {
  //   navigate(`/franchise/details/${id}`);
  // };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="md">
          <Box mb={4}>
            <CustomTitle>{t("My Franchises")}</CustomTitle>

            {stats && stats.overall && (
              <Box p={2} mb={3} bgcolor="#f5f5f5" borderRadius={1}>
                <Typography variant="h6">{t("Overview")}</Typography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={4}>
                    <Typography variant="body2">
                      {t("Total Franchises")}
                    </Typography>
                    <Typography variant="h6">
                      {stats.overall.total_franchises}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2">
                      {t("Total Farmers")}
                    </Typography>
                    <Typography variant="h6">
                      {stats.overall.total_farmers}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2">
                      {t("Total Earnings")}
                    </Typography>
                    <Typography variant="h6">
                      ${stats.overall.total_earnings.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" mb={2}>
              <CustomButton onClick={() => setShowAddForm(!showAddForm)}>
                {showAddForm ? t("Cancel") : t("Add Franchise")}
              </CustomButton>
            </Box>

            {showAddForm && (
              <Box mb={4} p={3} bgcolor="#f8f8f8" borderRadius={1}>
                <Typography variant="h6" mb={2}>
                  {t("Add New Franchise")}
                </Typography>
                <Grid container spacing={2}>
                  {/* Franchise Details Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" mb={1}>
                      {t("Franchise Details")}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("Franchise Name")}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>

                  {/* Owner Details Section */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      mt={2}
                      mb={1}
                    >
                      {t("Owner Details")}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Owner Full Name")}
                      name="owner_full_name"
                      value={formData.owner_full_name}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Owner Phone Number")}
                      name="owner_phone"
                      value={formData.owner_phone}
                      onChange={handleInputChange}
                      required
                      helperText={t(
                        "10-digit number without spaces or special characters"
                      )}
                      type="tel"
                      inputProps={{
                        maxLength: 10,
                        inputMode: "numeric",
                      }}
                      onKeyDown={handleMobileKeyDown}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+91</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Pricing Details Section */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      mt={2}
                      mb={1}
                    >
                      {t("Pricing Details")}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Basic Subscription Price")}
                      name="basic_subscription_price"
                      type="number"
                      value={formData.basic_subscription_price}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Premium Subscription Price")}
                      name="premium_subscription_price"
                      type="number"
                      value={formData.premium_subscription_price}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Soil Test Price")}
                      name="soiltest_price"
                      type="number"
                      value={formData.soiltest_price}
                      onChange={handleInputChange}
                    />
                  </Grid>

                  {/* Incentive Details Section */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      mt={2}
                      mb={1}
                    >
                      {t("Incentive Details")}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Basic Subscription Incentive")}
                      name="basic_subscription_incentive"
                      type="number"
                      value={formData.basic_subscription_incentive}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Premium Subscription Incentive")}
                      name="premium_subscription_incentive"
                      type="number"
                      value={formData.premium_subscription_incentive}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("Soil Test Incentive")}
                      name="soiltest_incentive"
                      type="number"
                      value={formData.soiltest_incentive}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                      <CustomButton onClick={handleAddFranchise}>
                        {t("Create Franchise")}
                      </CustomButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <div>
              {franchises.length === 0 ? (
                <Typography variant="subtitle1" textAlign="center">
                  {t("You haven't created any franchises yet.")}
                </Typography>
              ) : (
                franchises.map((franchise) => (
                  <CustomCard
                    key={franchise.id}
                    id={franchise.id}
                    t={t}
                    data={{
                      [t("Name")]: franchise.name,
                      [t(
                        "Basic Subscription"
                      )]: `₹${franchise.basic_subscription_price}`,
                      [t(
                        "Premium Subscription"
                      )]: `₹${franchise.premium_subscription_price}`,
                      [t("Soil Test")]: `₹${franchise.soiltest_price}`,
                      [t("Date Joined")]: new Date(
                        franchise.date_joined
                      ).toLocaleDateString(),
                    }}
                    disableChange
                    // handleView={() => handleViewFranchise(franchise.id)}
                  />
                ))
              )}
            </div>
          </Box>
        </Container>
      </LoaderContainer>
    </>
  );
}
